/**
 * DeckBrowser
 *
 * Decks-first view of a folder: lists every deck (and its descendants when
 * the folder has subfolders) as a collapsible row, with slides loaded
 * lazily on expand. Replaces the "fetch the entire folder's slides up
 * front, group by deck client-side" pattern for browse mode — search
 * results still go through SlideLibrary's flat grid.
 *
 * State the parent owns: which decks are open. State this component owns:
 * which deck's slides are currently expanded (and therefore being fetched).
 */

import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  SlideSenseDeck,
  SlideSenseFolder,
  SlideSenseSlide,
} from "@/features/slide-sense/types"

import { Inbox, Loader2 } from "@/components/icons"
import { Skeleton } from "@/components/ui/skeleton"

import { DeckSection } from "./DeckSection"
import { useDecks } from "../hooks/use-decks"

export interface DeckBrowserProps {
  /** Folder being browsed. ``undefined`` means "Alla slides" (root). */
  folderId?: string
  /** Used to label each deck row with its containing folder name. */
  folders: SlideSenseFolder[]
  onPreview?: (slide: SlideSenseSlide, navigationContext: SlideSenseSlide[]) => void
  onAdd?: (slide: SlideSenseSlide) => void
  onAddDeck?: (slides: SlideSenseSlide[]) => void
}

/**
 * Rank every folder by a pre-order walk of the tree, visiting siblings in
 * admin-defined `sort_order`. Gives a single linear ordering so the flat
 * "all slides" deck list can be grouped to mirror the folder structure the
 * admin arranged. Folders not present in the list rank last.
 */
function buildFolderRank(folders: SlideSenseFolder[]): Map<string, number> {
  const childrenByParent = new Map<string | null, SlideSenseFolder[]>()
  for (const f of folders) {
    const key = f.parent_folder_id ?? null
    const siblings = childrenByParent.get(key) ?? []
    siblings.push(f)
    childrenByParent.set(key, siblings)
  }
  for (const siblings of childrenByParent.values()) {
    siblings.sort((a, b) => a.sort_order - b.sort_order)
  }

  const rank = new Map<string, number>()
  let next = 0
  const visit = (parent: string | null) => {
    for (const folder of childrenByParent.get(parent) ?? []) {
      rank.set(folder.id, next++)
      visit(folder.id)
    }
  }
  visit(null)
  return rank
}

export function DeckBrowser({ folderId, folders, onPreview, onAdd, onAddDeck }: DeckBrowserProps) {
  const { t } = useTranslation()
  const { data: decks, isLoading, isError } = useDecks({ folderId })

  // Order decks to mirror the admin's folder arrangement: folder rank first,
  // then deck sort_order ASC and newest-first within each folder (matches
  // list_decks on the backend and AdminFileTree's buildDeckMap). Sorting
  // explicitly by sort_order — rather than relying on the backend array order —
  // means optimistic cache patches from a deck reorder reorder this view
  // immediately, without waiting for a refetch. Unknown folders sort last.
  const folderRank = useMemo(() => buildFolderRank(folders), [folders])
  const orderedDecks = useMemo(() => {
    if (!decks) return decks
    return [...decks].sort((a, b) => {
      const folderDiff =
        (folderRank.get(a.folder_id) ?? Number.MAX_SAFE_INTEGER) -
        (folderRank.get(b.folder_id) ?? Number.MAX_SAFE_INTEGER)
      if (folderDiff !== 0) return folderDiff
      const sortDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0)
      if (sortDiff !== 0) return sortDiff
      return b.created_at.localeCompare(a.created_at)
    })
  }, [decks, folderRank])

  // Open-state per deck. Empty by default — every deck starts collapsed,
  // so opening the folder fires one cheap "list decks" query and nothing
  // else until the user expands a row.
  const [openDeckIds, setOpenDeckIds] = useState<Set<string>>(new Set())
  const toggleDeck = (deckId: string) => {
    setOpenDeckIds((prev) => {
      const next = new Set(prev)
      if (next.has(deckId)) next.delete(deckId)
      else next.add(deckId)
      return next
    })
  }

  const folderNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const f of folders) map.set(f.id, f.name)
    return map
  }, [folders])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm">
        <Loader2 className="text-muted-foreground/40 h-6 w-6" />
        <p className="text-muted-foreground">{t("slideSense.failedToLoadSlides")}</p>
      </div>
    )
  }

  if (!orderedDecks || orderedDecks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <div className="bg-muted/60 flex h-14 w-14 items-center justify-center rounded-full">
          <Inbox className="text-muted-foreground h-7 w-7" />
        </div>
        <p className="text-muted-foreground text-sm">{t("slideSense.noSlidesFound")}</p>
      </div>
    )
  }

  return (
    <div>
      {orderedDecks.map((deck) => (
        <DeckSection
          key={deck.id}
          deck={deck}
          folderName={folderNameById.get(deck.folder_id) ?? null}
          open={openDeckIds.has(deck.id)}
          onAdd={onAdd}
          onAddDeck={onAddDeck}
          onPreview={(slide, deckSlides) => onPreview?.(slide, deckSlides)}
          onToggleOpen={() => toggleDeck(deck.id)}
        />
      ))}
    </div>
  )
}

// Re-export so callers can also use the section directly if they want.
export { DeckSection }
export type { SlideSenseDeck }
